import React, { useEffect, useState, lazy, Suspense, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TopNavbar from './TopNavbar';
import Sidebar from './Sidebar';
import Footer from './Footer';

const OnboardingTour = lazy(() => import('../Onboarding/OnboardingTour'));

const ApplicationShell = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem('sidebar_collapsed') === 'true';
        } catch (_) {
            return false;
        }
    });

    const { user } = useAuth();

    const handleToggleCollapse = useCallback(() => {
        setIsCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem('sidebar_collapsed', String(next));
            } catch (_) {}
            return next;
        });
    }, []);

    // Keyboard shortcut (Ctrl+\ or Cmd+\) to toggle desktop sidebar collapse
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === '\\') {
                e.preventDefault();
                handleToggleCollapse();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleToggleCollapse]);

    // First-run tour. We delay mounting until:
    //  (a) a user is authenticated (no point tour-ing the login page),
    //  (b) localStorage says onboarding isn't completed,
    //  (c) the shell has rendered at least one frame so anchors exist.
    const [showTour, setShowTour] = useState(false);
    useEffect(() => {
        if (!user) return undefined;
        // `?tour=1` forces a replay for anyone who wants to see the
        // walkthrough again. We strip the param after kicking off so
        // bookmarks/shares don't keep retriggering it.
        const params = new URLSearchParams(window.location.search);
        if (params.get('tour') === '1') {
            try { localStorage.removeItem('onboarding_completed'); } catch (_) { /* ignore */ }
            params.delete('tour');
            const qs = params.toString();
            const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
            window.history.replaceState({}, '', next);
        }
        let completed = false;
        try { completed = localStorage.getItem('onboarding_completed') === '1'; } catch (_) { completed = true; }
        if (completed) return undefined;
        const t = setTimeout(() => setShowTour(true), 400);
        return () => clearTimeout(t);
    }, [user]);

    return (
        <div className="flex h-[100dvh] min-h-0 bg-gray-50 overflow-hidden">
            {/* Sidebar */}
            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                isCollapsed={isCollapsed}
                onToggleCollapse={handleToggleCollapse}
            />

            {/* Main column: only this area scrolls; sidebar stays fixed in place on desktop */}
            <div className="flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden">
                <TopNavbar onMenuClick={() => setSidebarOpen(true)} />

                <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 overscroll-y-contain">
                    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col min-h-full">
                        <div className="flex-1">{children}</div>
                        <Footer variant="app" />
                    </div>
                </main>
            </div>

            {showTour && (
                <Suspense fallback={null}>
                    <OnboardingTour onDone={() => setShowTour(false)} />
                </Suspense>
            )}
        </div>
    );
};

export default ApplicationShell;
